// src/components/Dashboard.js
import React, { useEffect, useState } from 'react';

const Dashboard = () => {
  const [userEmail, setUserEmail] = useState('');
  const [userNome, setUserNome] = useState('');
  const [userLogin, setUserLogin] = useState('');
  const [firstName, setFirstName] = useState('');
  const [userPermissoes, setUserPermissoes] = useState([]);

  useEffect(() => {
    let cancelled = false;

    // assets locais
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '/assets/styles.css';
    document.head.appendChild(link);

    document.body.style.backgroundImage = "url('/assets/wallpaperdash.jpg')";
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'center';
    document.body.style.backgroundRepeat = 'no-repeat';

    function setCookie(name, value, dias = 7) {
      const expires = new Date(Date.now() + dias*24*60*60*1000).toUTCString();
      document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
    }

    function clearLocalSession() {
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userNome');
      localStorage.removeItem('userLogin');
      localStorage.removeItem('userPermissoes');
      localStorage.removeItem('ssoToken');
    }

    function applyLocalProfile() {
      const email = localStorage.getItem('userEmail') || '';
      const nome  = localStorage.getItem('userNome')  || '';
      const login = localStorage.getItem('userLogin') || '';

      setUserEmail(email);
      setUserNome(nome);
      setUserLogin(login);

      setCookie('userEmail', email);
      setCookie('userNome',  nome || email);

      if (nome) {
        const part = nome.split(' ')[0];
        setFirstName(part.charAt(0).toUpperCase() + part.slice(1));
      } else if (email) {
        const part = email.split('@')[0];
        setFirstName(part.charAt(0).toUpperCase() + part.slice(1));
      }

      const stored = localStorage.getItem('userPermissoes');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          const arr = Array.isArray(parsed) ? parsed : [parsed];
          setUserPermissoes(arr.map((p) => String(p || '').trim().toLowerCase()).filter(Boolean));
        } catch {
          setUserPermissoes([String(stored).trim().toLowerCase()].filter(Boolean));
        }
      }
    }

    // O hub usa localStorage para montar os cards, mas /score exige cookie sid
    // (auth em memória). Se a sessão morreu (restart do auth), o card aparece
    // e o clique só devolve para o login — validar /auth/me ao abrir.
    applyLocalProfile();

    (async () => {
      try {
        const r = await fetch('/auth/me', { credentials: 'same-origin' });
        if (cancelled) return;
        if (r.status === 401 || r.status === 403) {
          clearLocalSession();
          window.location.replace('/?redirect=/dashboard');
          return;
        }
        if (!r.ok) return;
        const data = await r.json();
        const email = data.email || data.userEmail || '';
        if (!email) {
          clearLocalSession();
          window.location.replace('/?redirect=/dashboard');
          return;
        }
        const nome = data.userNome || data.nome || '';
        const login = data.userLogin || data.login || '';
        const perms = data.permissoes || data.perms || [];
        localStorage.setItem('userEmail', email);
        localStorage.setItem('userNome', nome);
        localStorage.setItem('userLogin', login);
        localStorage.setItem('userPermissoes', JSON.stringify(perms));
        applyLocalProfile();
      } catch (_) {
        // rede instável: mantém UI local; o clique em /score ainda exige sid
      }
    })();

    return () => {
      cancelled = true;
      document.head.removeChild(link);
      document.body.style.backgroundImage = null;
      document.body.style.backgroundSize = null;
      document.body.style.backgroundPosition = null;
      document.body.style.backgroundRepeat = null;
    };
  }, []);

  // ===== permissões =====
  const canAdmin           = userPermissoes.includes('admin');
  const canSuporte         = userPermissoes.includes('admin') || userPermissoes.includes('suporte');
  const canLogistica       = userPermissoes.includes('admin') || userPermissoes.includes('logistica');
  const canApr             = userPermissoes.includes('admin') || userPermissoes.includes('apr');
  const canComercial       = userPermissoes.includes('admin') || userPermissoes.includes('comercial');
  const canAprChecklist    = userPermissoes.includes('admin') || userPermissoes.includes('apr');
  const canAprEditor       = userPermissoes.includes('admin') || userPermissoes.includes('apreditor');
  const canAprViewer       = userPermissoes.includes('admin') || userPermissoes.includes('aprviewer');
  const canAprGrafico      = userPermissoes.includes('admin') || userPermissoes.includes('apr') || userPermissoes.includes('aprgrafico');
  const canRh              = userPermissoes.includes('admin') || userPermissoes.includes('rh');
  const canFinanceiro      = userPermissoes.includes('admin') || userPermissoes.includes('financeiro');
  const canFinanceiroAdm   = userPermissoes.includes('admin') || userPermissoes.includes('financeiroadm');
  const canFinanceiroScore = canFinanceiroAdm;
  const canCheckup         = userPermissoes.includes('admin') || userPermissoes.includes('checkup');
  const canCompras         = userPermissoes.includes('admin') || userPermissoes.includes('compras') || userPermissoes.includes('compras-admin');
  const canClientes        = userPermissoes.includes('admin') || userPermissoes.includes('comercial') || userPermissoes.includes('suporte');
  const canMarketing       = userPermissoes.includes('admin') || userPermissoes.includes('marketing') || userPermissoes.includes('supervisor');
  const canSolicitacoes    = userPermissoes.includes('admin') || userPermissoes.includes('desenvolvimento');

  // Vendedora: perfil "vendedor" sem financeiro/admin/financeiroadm — um card no hub.
  const permsNorm = userPermissoes.map((p) => String(p || '').trim().toLowerCase()).filter(Boolean);
  const hasPerm = (p) => permsNorm.includes(String(p).toLowerCase());
  const soApenasVendedora =
    hasPerm('vendedor') &&
    !hasPerm('admin') &&
    !hasPerm('financeiroadm') &&
    !hasPerm('financeiro');
  const canPainelVendedorasConsulta =
    soApenasVendedora ||
    hasPerm('admin') ||
    hasPerm('financeiroadm') ||
    hasPerm('financeiro') ||
    hasPerm('vendedor');

  // ===== redirecionamentos =====
  const handleAdminClick            = () => window.location.href = '/admin';
  const handleSuporteClick = () => {
    const token = localStorage.getItem('ssoToken');
    const baseUrl = 'https://sonikcenter.sonik.cloud/suporte-v2/';
    const url = token ? `${baseUrl}?sso_token=${encodeURIComponent(token)}` : baseUrl;
    window.location.href = url;
  };
  const handleSuporteAntigoClick    = () => window.location.href = 'http://sonikcenter.sonik.cloud:3536/';
  const handleLogisticaClick        = () => window.location.href = 'http://sonikcenter.sonik.cloud:4782/';
  const handleFrotaClick            = () => window.location.href = 'https://sonikcenter.sonik.cloud/frota';
  const handleAprClick              = () => window.location.href = 'http://sonikcenter.sonik.cloud:3008/';
  const handleComercialClick        = () => window.location.href = 'http://sonikcenter.sonik.cloud:8088/';

  // APR nova (4550)
  const handleAprChecklistClick     = () => window.location.href = 'http://sonikcenter.sonik.cloud/apr-admin/aprs.html';
  const handleAprEditorClick        = () => window.location.href = '/apr-admin/index.html';
  const handleAprDashboardClick     = () => window.location.href = '/apr-admin/dashboard-aprs.html';
  const handleAprViewerClick        = () => window.location.href = 'http://sonikcenter.sonik.cloud:4550/admin/aprs.html';
  const handleAprLogClick           = () => window.location.href = '/apr-admin/logs.html';

  // Admin legacy (4544)
  const handleDashHtmlClick         = () => window.location.href = 'http://sonikcenter.sonik.cloud:4544/admin/dashboard.html';
  const handleEditorHtmlClick       = () => window.location.href = 'http://sonikcenter.sonik.cloud:4544/admin/editor.html';
  const handleCarEditorHtmlClick    = () => window.location.href = 'http://sonikcenter.sonik.cloud:4544/admin/car-editor.html';

  // Dev (7774)
  const handleDevClick              = () => window.location.href = 'http://sonikcenter.sonik.cloud:7774/login.html';

  // RH (7007)
  const handleRhClick               = () => window.location.href = 'http://sonikcenter.sonik.cloud/email/';

  // Benefícios (1905)
  const handleBeneficiosClick       = () => window.location.href = '/beneficios/';

  // Score (5087)
  const handlePainelScoreClick      = () => window.location.href = '/score';
  const handlePainelScoreAdminClick = () => window.location.href = '/score/admin-score.html';
  const handleScoreComercialClick   = () => window.location.href = '/score/finance-vendedoras';
  const handlePainelVendedorasClick = () => window.location.href = '/score/vendedoras-consulta';

  // Painel Financeiro (5088) – só admin
  const handlePainelFinanceiroClick = () => window.location.href = '/financeiro/';

  // Cadastro (5897 → /cadastro) – só admin
  const handleCadastroClick = () => window.location.href = '/cadastro/';

  // Quiz Semanal (/quiz) – admin e apreditor
  const handleQuizClick = () => window.location.href = '/quiz/';

  // Painel de Compras (7020 → /compras) – cargos compras e compras-admin
  const handleComprasClick = () => window.location.href = '/compras/';

  // Solicitacoes internas (7021 → /solicitacoes) – admin e desenvolvimento (teste)
  const handleSolicitacoesClick = () => window.location.href = '/solicitacoes/';

  // Marketing Center (/marketing) – admin, marketing e supervisor (alias)
  const handleMarketingClick = () => window.location.href = '/marketing/';

  // Solicitar desenvolvimento (IP puro) — visível para todos
  const handleDesenvolvimentoClick  = () => window.location.href = 'http://131.255.179.139:7774/login.html';
  // Se preferir abrir em nova aba, troque por:
  // const handleDesenvolvimentoClick = () => window.open('http://131.255.179.139:7774/login.html', '_blank', 'noopener,noreferrer');

  // Gift Card (4173) — visível apenas para admin
  const handleGiftCardClick = () => window.location.href = 'http://131.255.179.139:4173/';

  // Busca de Clientes (/clientes) — admin, comercial, suporte
  const handleClientSearchClick = () => window.location.href = '/clientes/';

  const handleLogout = () => {
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userNome');
    localStorage.removeItem('userLogin');
    localStorage.removeItem('userPermissoes');
    localStorage.removeItem('ssoToken');
    window.location.href = '/';
  };

  // ===== estilos =====
  const containerStyle = {
    width: '80%', margin: '20px auto', background: 'rgba(255, 255, 255, 0.9)',
    padding: '20px', boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)', textAlign: 'center',
    marginTop: '50px', position: 'relative', minHeight: '80vh', borderRadius: '8px',
    maxHeight: '80vh', overflowY: 'auto',
  };
  const appsContainerStyle = {
    display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '20px', marginTop: '30px'
  };
  const appCardStyle = {
    background: '#fff', border: '1px solid #ccc', borderRadius: '8px',
    width: '180px', height: '250px', padding: '16px', textAlign: 'center',
    cursor: 'pointer', boxShadow: '0 0 6px rgba(0,0,0,0.1)', transition: 'transform 0.2s',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', gap: '12px'
  };
  const appCardImgStyle = { width: '150px', height: '150px', objectFit: 'cover', marginBottom: '10px' };
  const appTitleStyle   = { margin: 0, fontSize: '18px', lineHeight: '1.2', minHeight: '38px' };
  const logoutButtonStyle = {
    position: 'fixed', top: '10px', right: '10px',
    padding: '8px 12px', background: '#007bff', color: '#fff',
    border: 'none', borderRadius: '4px', cursor: 'pointer'
  };
  const footerStyle = {
    position: 'fixed', bottom: '0', left: '0', padding: '10px', background: '#f4f4f9', fontSize: '12px', color: '#555'
  };

  return (
    <div style={containerStyle}>
      <button style={logoutButtonStyle} onClick={handleLogout}>Logout</button>
      <h1>Bem-vindo{firstName ? `, ${firstName}` : ''} ao Painel Sonik</h1>
      {soApenasVendedora ? (
        <p>Consulte o CPF e faça o cadastro pelo painel. A venda fica no seu nome.</p>
      ) : (
        <p>Selecione a opção desejada:</p>
      )}

      <div style={appsContainerStyle}>

        {/* Vendedora: painel de venda (cadastro/contrato no próprio nome) */}
        {soApenasVendedora && (
          <div style={appCardStyle} onClick={handleScoreComercialClick}>
            <img src="/assets/scoreadmin.png" alt="Painel Score" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Painel Score</h3>
          </div>
        )}

        {/* Demais apps — ocultos para perfil só vendedora */}
        {canSolicitacoes && (
          <div style={appCardStyle} onClick={handleSolicitacoesClick}>
            <img
              src="/assets/dev.png"
              alt="Solicitações"
              style={appCardImgStyle}
              onError={(e) => { e.currentTarget.src = '/assets/cadastro.png'; }}
            />
            <h3 style={appTitleStyle}>Solicitações</h3>
          </div>
        )}

        {!soApenasVendedora && (
          <div style={appCardStyle} onClick={handleDesenvolvimentoClick}>
            <img src="/assets/dev.png" alt="Solicitar Desenvolvimento" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Solicitar Desenvolvimento</h3>
          </div>
        )}

        {!soApenasVendedora && canSuporte && (
          <div style={appCardStyle} onClick={handleSuporteClick}>
            <img src="/assets/suporte.png" alt="Suporte App" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Suporte App</h3>
          </div>
        )}

        {canAdmin && (
          <div style={appCardStyle} onClick={handleSuporteAntigoClick}>
            <img src="/assets/suporte.png" alt="Suporte antigo" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Suporte (antigo)</h3>
          </div>
        )}

        {canAdmin && (
          <div style={appCardStyle} onClick={handleBeneficiosClick}>
            <img src="/assets/sva.png" alt="Benefícios" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Benefícios</h3>
          </div>
        )}

        {canLogistica && (
          <div style={appCardStyle} onClick={handleLogisticaClick}>
            <img src="/assets/iconlog.png" alt="Logística" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Logística</h3>
          </div>
        )}

        {canLogistica && (
          <div style={appCardStyle} onClick={handleFrotaClick}>
            <img src="/assets/frota.png" alt="Frota" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Frota</h3>
          </div>
        )}

        {canAdmin && (
          <div style={appCardStyle} onClick={handleAprClick}>
            <img src="/assets/iconapr.jpg" alt="APR" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>APR antigo</h3>
          </div>
        )}

        {canComercial && (
          <div style={appCardStyle} onClick={handleComercialClick}>
            <img src="/assets/iconcomercial.png" alt="Comercial" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Comercial</h3>
          </div>
        )}

        {canClientes && (
          <div style={appCardStyle} onClick={handleClientSearchClick}>
            <img src="/assets/dev.png" alt="Busca de Clientes" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Busca de Clientes</h3>
          </div>
        )}

        {(canAprChecklist || canAprViewer) && (
          <div style={appCardStyle} onClick={handleAprChecklistClick}>
            <img src="/assets/apricon.png" alt="Checklist APR" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Checklist APR</h3>
          </div>
        )}

        {canAprEditor && (
          <div style={appCardStyle} onClick={handleAprEditorClick}>
            <img src="/assets/apreditor.png" alt="Editor Perguntas" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Editor de perguntas APR</h3>
          </div>
        )}

        {canAprEditor && (
          <div style={appCardStyle} onClick={handleQuizClick}>
            <img
              src="/assets/quizicon.png"
              alt="Quiz"
              style={appCardImgStyle}
              onError={(e) => { e.currentTarget.src = '/assets/checkup.png'; }}
            />
            <h3 style={appTitleStyle}>Quiz</h3>
          </div>
        )}

        {canAprGrafico && (
          <div style={appCardStyle} onClick={handleAprDashboardClick}>
            <img src="/assets/aprdashboard.png" alt="Gráficos das APRs" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Gráficos das APRs</h3>
          </div>
        )}

        {canFinanceiroScore && (
          <div style={appCardStyle} onClick={handlePainelScoreClick}>
            <img src="/assets/scoreicon.png" alt="Painel Score Admin" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Painel Score Admin</h3>
          </div>
        )}

        {canAdmin && (
          <div style={appCardStyle} onClick={handlePainelScoreAdminClick}>
            <img src="/assets/reguaicon.png" alt="Painel Editor de Score" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Painel Editor de Score</h3>
          </div>
        )}

        {canAdmin && (
          <div style={appCardStyle} onClick={handleGiftCardClick}>
            <img src="/assets/gifticon.png" alt="Gift Card" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Gift Card</h3>
          </div>
        )}

        {canAdmin && (
          <div style={appCardStyle} onClick={handlePainelFinanceiroClick}>
            <img src="/assets/scoreicon.png" alt="Painel Financeiro" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Painel Financeiro</h3>
          </div>
        )}

        {canAprEditor && (
          <div style={appCardStyle} onClick={handleCadastroClick}>
            <img src="/assets/cadastro.png" alt="Cadastro" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Cadastro</h3>
          </div>
        )}

        {canCompras && (
          <div style={appCardStyle} onClick={handleComprasClick}>
            <img
              src="/assets/compras.png"
              alt="Compras"
              style={appCardImgStyle}
              onError={(e) => { e.currentTarget.src = '/assets/cadastro.png'; }}
            />
            <h3 style={appTitleStyle}>Compras</h3>
          </div>
        )}

        {canFinanceiro && (
          <div style={appCardStyle} onClick={handleScoreComercialClick}>
            <img src="/assets/scoreadmin.png" alt="Painel Score" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Painel Score</h3>
          </div>
        )}

        {!soApenasVendedora && canPainelVendedorasConsulta && (
          <div style={appCardStyle} onClick={handlePainelVendedorasClick}>
            <img src="/assets/scoreadmin.png" alt="Painel Vendedoras" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Painel Vendedoras</h3>
          </div>
        )}

        {canMarketing && (
          <div style={appCardStyle} onClick={handleMarketingClick}>
            <img
              src="/assets/iconcomercial.png"
              alt="Marketing Center"
              style={appCardImgStyle}
              onError={(e) => { e.currentTarget.src = '/assets/cadastro.png'; }}
            />
            <h3 style={appTitleStyle}>Marketing Center</h3>
          </div>
        )}

        {canAdmin && (
          <div style={appCardStyle} onClick={() => window.location.href = '/admin-painel'}>
            <img src="/assets/iconadmin.png" alt="Administração" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Administração</h3>
          </div>
        )}

        {(canAprEditor || canCheckup) && (
          <div style={appCardStyle} onClick={handleDashHtmlClick}>
            <img src="/assets/checkup.png" alt="Dashboard HTML" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Últimos Checkups</h3>
          </div>
        )}

        {canAprEditor && (
          <>
            <div style={{ flexBasis: '100%', height: '1px', background: '#ddd', margin: '40px 0' }} />

            <div style={appCardStyle} onClick={handleEditorHtmlClick}>
              <img src="/assets/checklist.png" alt="Editor HTML" style={appCardImgStyle} />
              <h3 style={appTitleStyle}>Carros - Checklist</h3>
            </div>

            <div style={appCardStyle} onClick={handleCarEditorHtmlClick}>
              <img src="/assets/editor.png" alt="Car Editor HTML" style={appCardImgStyle} />
              <h3 style={appTitleStyle}>Carros - Editor</h3>
            </div>
          </>
        )}

        {canRh && (
          <div style={appCardStyle} onClick={handleRhClick}>
            <img src="/assets/rh.png" alt="RH" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Criação de Email</h3>
          </div>
        )}

        {canApr && (
          <div style={appCardStyle} onClick={handleAprLogClick}>
            <img src="/assets/logapricon.png" alt="Logs Apr" style={appCardImgStyle} />
            <h3 style={appTitleStyle}>Logs APR</h3>
          </div>
        )}

        {!canSuporte && !canLogistica && !canApr && !canAdmin && !canComercial && !canAprChecklist && !canAprGrafico && !canAprEditor && !canAprViewer && !canRh && !canFinanceiro && !canFinanceiroScore && !canCheckup && !soApenasVendedora && (
          <p>Você não possui permissões para visualizar nenhum app. Por favor, contate o administrador.</p>
        )}
      </div>

      <div style={footerStyle}>Logado como {userLogin || userEmail}</div>
    </div>
  );
};

export default Dashboard;
